import React, { useState } from 'react';
import AppleLogin from 'react-apple-login';
import { Link, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { CCSRFToken, CSAUTHID, CSAUTHToken, CSTEMPUSERID } from 'libs/api/apiClient';
import { handleFieldError, moveToFirstError } from 'libs/api/errorHandle';
import { userKakaoLogin, userLogin } from 'libs/api/user';
import { IKakaoLoginInfo, TUserLogin, TKakaoLogin } from 'modules/user';
import { ERoute } from 'enums/route.enum';
import { Button, Form, Input, Row, Col, Typography } from 'antd';
import { FormWrapper, SecondaryColor } from 'GlobalStyles';
import LoginBackgroundSource from 'assets/images/banners/login.webp';
import LoginKakaoSource from 'assets/images/pages/login/login_kakao.png';
import LoginAppleSource from 'assets/images/pages/login/login_apple.png';

const { Title, Text } = Typography;
const Login = () => {
  const [form] = Form.useForm();
  const next = queryString.parse(useLocation().search)?.next?.toString();
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

  const onClickLogin = () => {
    form
      .validateFields()
      .then((value) => {
        handleLogin(value as TUserLogin);
      })
      .catch((errorInfo) => {
        moveToFirstError(form, errorInfo.errorFields);
      });
  };
  const handleLogin = async (value: TUserLogin) => {
    try {
      setSubmitButtonDisabled(true);
      const response = await userLogin({
        userid: value.userid,
        password: value.password,
        is_remember: value.is_remember,
      });
      const authorization = response.headers.authorization.split('Basic ')[1];
      window.localStorage.setItem(CCSRFToken, authorization);
      window.location.href = next ? next : '/';
    } catch (e) {
      handleFieldError(e, form);
      throw e;
    } finally {
      setSubmitButtonDisabled(false);
    }
  };
  const onClickKakaoLogin = () => {
    handleKakaoLogin();
  };
  const handleKakaoLogin = async () => {
    try {
      setSubmitButtonDisabled(true);
      if (!Kakao.isInitialized()) {
        console.log('kakao key => ', process.env.REACT_APP_KAKAO_KEY);
        Kakao.init(process.env.REACT_APP_KAKAO_KEY ?? 'cda75f3cd4112396004aca5d7da58bc8');
      }
      Kakao.Auth.login({
        success: async (authObj: Kakao.Auth.AuthSuccessObject): Promise<void> => {
          console.log(`kakao token => ${authObj.access_token}`);
          const kakaoAccessToken: IKakaoLoginInfo = {
            kakao_access_token: authObj.access_token,
          };
          const response = await userKakaoLogin({
            type: 'kakao',
            token: `Bearer ${kakaoAccessToken.kakao_access_token!}`,
            code: 'web',
            userid: 'dummy@dummy.com',
            password: 'dummy',
            country_code: '82', // TODO::
          });
          console.log('response status => ', response.status);
          if (response.status === 203) {
            const authid = response.data.id;
            const userid = response.data.email;
            window.localStorage.setItem(CSAUTHID, authid);
            window.localStorage.setItem(CSTEMPUSERID, userid);
            window.localStorage.setItem(CSAUTHToken, `Bearer ${kakaoAccessToken.kakao_access_token!}`);
            window.location.href = '/user/signup?type=kakao';
          } else {
            console.log('response => ', response.data.token);
            const authorization = response.headers.authorization.split('Basic ')[1];
            window.localStorage.setItem(CCSRFToken, authorization);
            window.location.href = next ? next : '/';
          }
          setSubmitButtonDisabled(false);
        },
        fail: (err: Kakao.Auth.AuthError): void => {
          console.log('fail', err);
          setSubmitButtonDisabled(false);
        },
      });
    } catch (e) {
      handleFieldError(e, form);
      throw e;
    } finally {
      setSubmitButtonDisabled(false);
    }
  };

  const appleResponse = async (response: any) => {
    console.log(response);
    if (!response.error) {
      const code = response.authorization.code;
      const id_token = response.authorization.id_token;
      //console.log(`code ==> ${code}`)
      //console.log(`id_token ==> ${id_token}`);

      try {
        setSubmitButtonDisabled(true);

        const response = await userKakaoLogin({
          type: 'apple',
          token: id_token,
          code: 'web',
          userid: 'dummy@dummy.com',
          password: 'dummy',
          country_code: '82', // TODO::
        });
        console.log('response status => ', response.status);
        if (response.status === 203) {
          const authid = response.data.id;
          const userid = response.data.email;
          window.localStorage.setItem(CSAUTHID, authid);
          window.localStorage.setItem(CSTEMPUSERID, userid);
          window.localStorage.setItem(CSAUTHToken, id_token);
          window.location.href = '/user/signup?type=apple';
        } else {
          console.log('response => ', response.data.token);
          const authorization = response.headers.authorization.split('Basic ')[1];
          window.localStorage.setItem(CCSRFToken, authorization);
          window.location.href = next ? next : '/';
        }
      } catch (e) {
        handleFieldError(e, form);
        throw e;
      } finally {
        setSubmitButtonDisabled(false);
      }
    }
  };

  return (
    <Row style={{ maxWidth: '1440px', margin: '0 auto' }}>
      <Col xs={0} sm={0} md={0} lg={12} xl={12}>
        <img alt='' src={LoginBackgroundSource} style={{ width: '100%', height: '100%' }} />
      </Col>
      <Col xs={24} sm={24} md={24} lg={12} xl={12}>
        <FormWrapper style={{ maxWidth: '280px' }}>
          <Title level={2}>로그인</Title>
          <Text style={{ marginBottom: '30px', display: 'inline-block' }}>
            Career Step에 오신것을 환영합니다.
          </Text>
          <Form
            form={form}
            name='login'
            layout='vertical'
            className='form-inline'
            initialValues={{
              is_remember: true,
            }}
          >
            <Form.Item
              name={'userid'}
              className='form-item'
              label='아이디'
              rules={[
                {
                  type: 'email',
                  message: '이메일 형식이 올바르지 않습니다',
                },
                { required: true },
              ]}
            >
              <Input size='large' placeholder='이메일을 입력해주세요' />
            </Form.Item>
            <Form.Item name={'password'} className='form-item' label='비밀번호' rules={[{ required: true }]}>
              <Input.Password size='large' type='password' placeholder='비밀번호를 입력해주세요' />
            </Form.Item>
            <Form.Item shouldUpdate={true} className='form-button'>
              {() => (
                <Button
                  type='primary'
                  htmlType='submit'
                  size='large'
                  onClick={onClickLogin}
                  loading={submitButtonDisabled}
                >
                  로그인
                </Button>
              )}
            </Form.Item>
            <div className='form-bottom'>
              <Form.Item style={{ float: 'left' }}>
                <Link to={ERoute.UserPasswordReset}>비밀번호 및 아이디 찾기</Link>
              </Form.Item>
              <Form.Item style={{ float: 'right' }}>
                <Link to='/user/signup'>회원가입</Link>
              </Form.Item>
              { <Form.Item shouldUpdate={true} className='form-button'>
                {() => (
                  <Button
                    style={{
                      left: '-200px',
                      top: '20px',
                      width: '0px',
                      height: '0px'
                    }}
                    htmlType='submit'
                    size='large'
                    onClick={onClickKakaoLogin}
                    loading={submitButtonDisabled}
                  >
                    <img src={LoginKakaoSource} width="340" height="75"></img>
                  </Button>
                )}
                  </Form.Item> }
            </div>
            { <AppleLogin
              clientId='com.bigstepedu.careerstep.web'
              redirectURI='https://d9217rjptg82a.cloudfront.net/user/login'
              //redirectURI='https://c251-51-148-124-224.eu.ngrok.io/user/login'
              usePopup={true}
              callback={appleResponse} // Catch the response
              scope='email name'
              responseMode='query'
              render={(
                renderProps, //Custom Apple Sign in Button
              ) => (
                <Button
                    style={{
                      left: '-40px',
                      top: '20px',
                      width: '0px',
                      height: '0px'
                    }}
                    htmlType='submit'
                    size='large'
                    onClick={renderProps.onClick}
                    loading={submitButtonDisabled}
                  >
                  <img src={LoginAppleSource} width="320" height="75"></img>
                </Button>
              )}
                  />}
          </Form>
        </FormWrapper>
      </Col>
    </Row>
  );
};

export default Login;
